/**
 * Central locale-aware formatting (F5 Language Plan Phase 0).
 *
 * LAW (LANGUAGE_NJCASH.md §3): ALL user-facing currency/date/number
 * formatting goes through this module. Never write a new local
 * `formatCurrency()` in a component - scattered hardcoded 'en-US'/'USD'
 * copies are how BUG-073/BUG-074 happened (~40 drifting call sites migrated
 * here 2026-07-10).
 *
 * Locale follows the UI language (i18n). Decision D1: Thai is pinned to the
 * GREGORIAN calendar ('th-u-ca-gregory') - Intl's default for 'th' renders
 * Buddhist Era years (2026 -> 2569), which must never appear implicitly in
 * a finance app. Do not "clean up" that suffix.
 *
 * The UI locale controls separators/order/symbol placement; the TRANSACTION
 * or ACCOUNT controls the currency. Never infer currency from language.
 *
 * Owner-only pages (/dev, /metrics_dev, /log) intentionally keep their own
 * en-US formatting - they are outside translation scope (LANGUAGE_NJCASH §6).
 */

import { i18n } from '@/i18n';

const LOCALE_BY_LANGUAGE: Record<string, string> = {
  en: 'en-US',
  ja: 'ja',
  th: 'th-u-ca-gregory', // D1: Gregorian, NOT Buddhist Era
};

/** BCP-47 locale for the current UI language. */
export function currentLocale(): string {
  const lang = i18n.getLanguage();
  return LOCALE_BY_LANGUAGE[lang] ?? lang;
}

const CURRENCY_RE = /^[A-Za-z]{3}$/;

/**
 * Validate an ISO-4217 code; fall back instead of letting Intl throw a
 * RangeError (BUG-074's safety rule, now in exactly one place).
 */
export function safeCurrency(currency?: string, fallback = 'USD'): string {
  return currency && CURRENCY_RE.test(currency) ? currency.toUpperCase() : fallback;
}

/**
 * Format an amount in a currency.
 * No hardcoded fraction digits: Intl knows JPY has 0 decimals, USD/THB 2.
 * Pass options to override (e.g. { maximumFractionDigits: 0 } for compact
 * card displays).
 */
export function formatCurrency(
  amount: number,
  currency?: string,
  options?: Intl.NumberFormatOptions
): string {
  return new Intl.NumberFormat(currentLocale(), {
    style: 'currency',
    currency: safeCurrency(currency),
    ...options,
  }).format(amount);
}

/** Plain number in the current locale. */
export function formatNumber(value: number, options?: Intl.NumberFormatOptions): string {
  return new Intl.NumberFormat(currentLocale(), options).format(value);
}

/**
 * Compact currency for chart axis ticks: "$1.2K" (en), "￥1.2万" (ja).
 * Replaces hand-rolled `$${(v/1000).toFixed(1)}k` helpers (BUG-074 rule:
 * never inline a currency symbol).
 */
export function formatCurrencyCompact(amount: number, currency?: string): string {
  return new Intl.NumberFormat(currentLocale(), {
    style: 'currency',
    currency: safeCurrency(currency),
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(amount);
}

/** Date with explicit Intl options in the current locale. */
export function formatDate(date: Date, options: Intl.DateTimeFormatOptions): string {
  return new Intl.DateTimeFormat(currentLocale(), options).format(date);
}

// ------------------------------------------------------------
// Presets for the app's recurring shapes (keep names semantic so
// call sites read as intent, not Intl plumbing).
// ------------------------------------------------------------

/** "Jul 10" / ja "7月10日" */
export function formatMonthDay(date: Date): string {
  return formatDate(date, { month: 'short', day: 'numeric' });
}

/** "July 2026" / ja "2026年7月" */
export function formatMonthYear(date: Date): string {
  return formatDate(date, { month: 'long', year: 'numeric' });
}

/** "Jul 2026" */
export function formatMonthYearShort(date: Date): string {
  return formatDate(date, { month: 'short', year: 'numeric' });
}

/** "Jul" / "Jul 26" - analytics axis labels. */
export function formatMonthAxis(date: Date, includeYear: boolean): string {
  return formatDate(date, includeYear ? { month: 'short', year: '2-digit' } : { month: 'short' });
}

/**
 * NFKC-normalized, case-folded key for client-side search/filtering.
 * Unifies full-width ＡＢＣ with ABC and half-width with full-width katakana,
 * so Japanese descriptions match regardless of input width form.
 * Normalize BOTH the query and the data (LANGUAGE_MINDSET_WEBAPP.md §5).
 */
export function searchKey(value: string): string {
  return value.normalize('NFKC').toLowerCase();
}
